import { useState } from 'react';

/**
 * Komponent <Icon> — renderuje ikonę WebP z `assets/ui/` z auto-fallback'iem na emoji.
 *
 * Jeśli plik istnieje (i load się powiedzie) — używa go.
 * Jeśli nie istnieje (404 / load error) — używa emoji jako fallback.
 *
 * Dzięki temu możesz dorzucać kolejne ikony do `assets/ui/` w dowolnym
 * momencie i UI automatycznie z nich skorzysta po następnym odświeżeniu.
 *
 * Użycie:
 *   <Icon src="/ui/ui_coin.webp" fallback="🪙" className="w-7 h-7" />
 */
export interface IconProps {
  /** Ścieżka do ikony, np. "/ui/ui_coin.webp". */
  src: string;
  /** Emoji wyświetlane gdy PNG nie istnieje. */
  fallback: string;
  /**
   * Opcjonalna druga GRAFIKA próbowana zanim spadniemy na emoji. Użyteczne, gdy
   * docelowa ikona jest jeszcze niewygenerowana, a chcemy sensowny zamiennik-PNG
   * zamiast emoji (zgodne z zasadą „bez emoji w UI").
   */
  fallbackSrc?: string;
  /** Tailwind classes (rozmiar, margines itp.). */
  className?: string;
  /** Alt text dla accessibility. */
  alt?: string;
}

export function Icon({ src, fallback, fallbackSrc, className = '', alt }: IconProps) {
  const [failed, setFailed] = useState(false);
  const [usedFallbackSrc, setUsedFallbackSrc] = useState(false);

  // Grafiki UI są serwowane jako WebP (skrypt optimize-assets.mjs). Siatka
  // bezpieczeństwa: gdyby ktoś podał jeszcze `/ui/*.png`, normalizujemy końcówkę
  // na `.webp`. Gdy plik nie istnieje, `onError` próbuje `fallbackSrc` (inna
  // grafika), a dopiero potem fallback emoji.
  const finalSrc = (usedFallbackSrc && fallbackSrc ? fallbackSrc : src).replace(/\.png$/, '.webp');

  if (failed) {
    return (
      <span className={`inline-flex items-center justify-center ${className}`} aria-label={alt}>
        {fallback}
      </span>
    );
  }

  return (
    <img
      src={finalSrc}
      alt={alt ?? fallback}
      onError={() => {
        if (fallbackSrc && !usedFallbackSrc) setUsedFallbackSrc(true);
        else setFailed(true);
      }}
      className={`select-none pointer-events-none ${className}`}
      draggable={false}
    />
  );
}
